import React, { useState } from 'react';
import { X, CreditCard, Shield, Clock, DollarSign, ChevronRight, Loader2, HelpCircle } from 'lucide-react';
import { Button } from './ui/button';
import axios from 'axios';
import { useToast } from '../hooks/use-toast';

const API_URL = '';

/**
 * StripeSetupWizardModal
 * 
 * A friendly, simple modal that explains Stripe setup in plain language.
 * Shown after the monetization plan selection to guide new reviewers
 * through connecting their payment account.
 * 
 * Design principles:
 * - "10 year old" explanation - no jargon
 * - Emphasize speed and safety
 * - Make it feel easy, not scary
 */
// eslint-disable-next-line no-unused-vars
const StripeSetupWizardModal = ({ isOpen, onClose, onComplete, onSkip }) => {
  const { toast } = useToast();
  const [isConnecting, setIsConnecting] = useState(false);
  const [showFAQ, setShowFAQ] = useState(false);

  if (!isOpen) return null;

  const handleConnectStripe = async () => {
    setIsConnecting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(
        `${API_URL}/api/monetization/stripe/create-account-link`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Redirect to Stripe's hosted onboarding
      window.location.href = response.data.url;
    } catch (error) {
      console.error('Error connecting Stripe:', error);
      toast({
        title: 'Connection Error',
        description: 'Could not connect to Stripe. Please try again.',
        variant: 'destructive'
      });
      setIsConnecting(false);
    }
  };

  const handleSkip = () => {
    if (onSkip) onSkip();
    onClose();
  };

  return (
    <div 
      className="fixed inset-0 z-[99999] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4 overflow-y-auto"
      data-testid="stripe-setup-wizard-modal"
    >
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden my-4 max-h-[90vh] flex flex-col">
        {/* Header with gradient - fixed at top */}
        <div className="bg-gradient-to-r from-[#635BFF] to-[#8B5CF6] px-6 py-4 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <DollarSign className="w-6 h-6 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Get Paid for Reviews</h2>
                <p className="text-white/80 text-sm">One quick step to start earning</p>
              </div>
            </div>
            <button
              onClick={handleSkip}
              className="text-white/60 hover:text-white transition p-1"
              data-testid="stripe-wizard-close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Main Content - scrollable */}
        <div className="px-5 py-4 overflow-y-auto flex-1">
          {/* Simple Explanation */}
          <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-lg p-4 mb-4">
            <h3 className="font-semibold text-slate-800 mb-2 text-base">What is Stripe?</h3>
            <p className="text-slate-600 text-sm leading-relaxed">
              <strong>Stripe is like a digital wallet</strong> that collects money from artists 
              and sends it straight to your bank account. Used by Uber, Lyft, and millions of businesses.
            </p>
            <p className="text-slate-600 text-sm leading-relaxed mt-2">
              <strong>Connect once</strong> → artists pay you → money goes directly to your bank.
            </p>
          </div>

          {/* Benefits - more compact */}
          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-3 p-2.5 bg-green-50 rounded-lg border border-green-100">
              <div className="w-7 h-7 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Clock className="w-3.5 h-3.5 text-green-600" />
              </div>
              <div>
                <p className="font-medium text-green-800 text-sm">Takes about 2 minutes</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2.5 bg-blue-50 rounded-lg border border-blue-100">
              <div className="w-7 h-7 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <Shield className="w-3.5 h-3.5 text-blue-600" />
              </div>
              <div>
                <p className="font-medium text-blue-800 text-sm">100% Safe & Secure</p>
              </div>
            </div>

            <div className="flex items-center gap-3 p-2.5 bg-purple-50 rounded-lg border border-purple-100">
              <div className="w-7 h-7 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                <DollarSign className="w-3.5 h-3.5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-purple-800 text-sm">Get paid directly to your bank</p>
              </div>
            </div>
          </div>

          {/* What You'll Need - more compact */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
            <p className="font-medium text-amber-800 text-sm mb-1.5">What you'll need:</p>
            <ul className="text-xs text-amber-700 space-y-0.5">
              <li className="flex items-center gap-1.5">
                <ChevronRight className="w-3 h-3" />
                Bank account or debit card
              </li>
              <li className="flex items-center gap-1.5">
                <ChevronRight className="w-3 h-3" />
                Your address (for tax purposes)
              </li>
              <li className="flex items-center gap-1.5">
                <ChevronRight className="w-3 h-3" />
                Last 4 digits of SSN (US) or ID
              </li>
            </ul>
          </div>

          {/* FAQ Toggle */}
          <button
            onClick={() => setShowFAQ(!showFAQ)}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-700 text-sm mb-3 transition"
          >
            <HelpCircle className="w-4 h-4" />
            <span>{showFAQ ? 'Hide' : 'Show'} common questions</span>
          </button>

          {showFAQ && (
            <div className="bg-slate-50 rounded-lg p-3 mb-4 space-y-2 text-xs">
              <div>
                <p className="font-medium text-slate-800">Is this free?</p>
                <p className="text-slate-600">Yes! Small fee (2.9% + 30¢) only when you receive payments.</p>
              </div>
              <div>
                <p className="font-medium text-slate-800">What if I'm not in the US?</p>
                <p className="text-slate-600">Stripe works in 40+ countries with your local bank.</p>
              </div>
            </div>
          )}

          {/* CTA Buttons */}
          <div className="space-y-2">
            <Button
              onClick={handleConnectStripe}
              disabled={isConnecting}
              className="w-full py-5 text-base font-semibold bg-[#635BFF] hover:bg-[#5147E5] text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
              data-testid="stripe-connect-button"
            >
              {isConnecting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Connecting...
                </>
              ) : (
                <>
                  <CreditCard className="w-5 h-5 mr-2" />
                  Connect Stripe (2 min)
                </>
              )}
            </Button>

            <button
              onClick={handleSkip}
              className="w-full text-center text-slate-400 hover:text-slate-600 text-sm py-1.5 transition"
              data-testid="stripe-skip-button"
            >
              I'll do this later
            </button>
          </div>

          {/* Trust Badge */}
          <div className="flex items-center justify-center gap-2 mt-4 pt-3 border-t border-slate-100">
            <Shield className="w-3.5 h-3.5 text-slate-400" />
            <span className="text-xs text-slate-400">
              Secured by Stripe • Bank-level encryption
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StripeSetupWizardModal;
